// Finance charts use confirmed, continuous local periods in one selected currency.
// This module describes recorded amounts; it does not provide financial advice.
// Short/incomplete reporting periods are not projected into a financial forecast.

import { type Bucket, type Trend, type Granularity } from "./engine";
import type { DashboardConfig, MetricResult } from "./dashboard";
import { buildEvolutionPayload, type EvolutionPayload } from "./mirrorEvolutionFeed";
import type { FinancePeriod } from "@/hooks/useFinance";
import { formatMoneyShort } from "@/lib/finance/format";

const money = (v: number) => formatMoneyShort(v);

export const FINANCE_DASHBOARD: DashboardConfig = {
  moduleKey: "finance",
  titleKey: "finance.analytics.title",
  defaultGranularity: "month",
  metrics: [
    {
      key: "spend",
      labelKey: "finance.analytics.metric.spend",
      reducer: "sum", // periods are already aggregated
      color: "hsl(265 60% 65%)", // purple — NOT red; spend is descriptive, not alarming
      chart: "bar",
      projectable: false,
      format: money,
    },
    {
      key: "income",
      labelKey: "finance.analytics.metric.income",
      reducer: "sum",
      color: "hsl(187 72% 55%)",
      chart: "line",
      projectable: false,
      format: money,
    },
    {
      key: "net",
      labelKey: "finance.analytics.metric.net",
      reducer: "sum",
      color: "hsl(220 70% 62%)",
      chart: "line",
      projectable: false, // net crosses zero — a forecast would mislead
      format: money,
    },
  ],
};

// Last-vs-previous recorded period; an unfinished period remains descriptive.
function financeTrend(buckets: Bucket[]): Trend {
  const n = buckets.length;
  const current = n > 0 ? buckets[n - 1].value : 0;
  const previous = n > 1 ? buckets[n - 2].value : 0;
  const delta = current - previous;
  const pct = previous === 0 ? null : (delta / Math.abs(previous)) * 100;
  const direction = delta > 1e-9 ? "up" : delta < -1e-9 ? "down" : "flat";
  return { current, previous, delta, pct, direction };
}

/** Periods arrive newest-first, with one currency and continuous local dates. */
export function buildFinanceResults(periods: FinancePeriod[], currency = "BRL", locale = "pt-BR"): MetricResult[] {
  const asc = [...periods].reverse(); // chronological ascending
  return FINANCE_DASHBOARD.metrics.map((m) => {
    const pick = (p: FinancePeriod) =>
      m.key === "spend" ? p.expense : m.key === "income" ? p.income : p.net;
    const buckets: Bucket[] = asc.map((p) => ({
      key: p.period,
      // Civil date anchored at local noon.
      start: new Date(p.period + "T12:00:00").getTime(),
      value: pick(p),
      count: p.tx_count,
    }));
    const trend = financeTrend(buckets);
    return { config: { ...m, projectable: false, format: (value: number) => formatMoneyShort(value, currency, locale) }, buckets, trend, projection: null, streak: null };
  });
}

// Descriptive Mirror payload for future integration; no forecast or advice.
export function buildFinanceEvolution(
  periods: FinancePeriod[],
  granularity: Granularity = FINANCE_DASHBOARD.defaultGranularity,
): EvolutionPayload {
  return buildEvolutionPayload(buildFinanceResults(periods), "finance", granularity);
}
